// frontend/src/pages/Rosters/RosterCreatePage.js
// Build a roster: Name + pick students with filters like Award Points → Student Select.
// Pure JS. Uses only your existing `api` client.
//
// GET students (first success wins, supports q, house_id, grade, page, page_size):
//   /api/students/ | /api/students | /api/learners | /api/pupils | /api/users/students
//
// CREATE roster (best-effort):
//   1) POST /api/rosters { name, description?, student_ids }  OR { name, description?, studentIds }
//   2) POST /api/rosters { name, description? } THEN POST /api/rosters/:id/(students|members) { student_ids }
//   3) POST /api/groups  { name, description? } THEN POST /api/groups/:id/(students|members)  { student_ids }

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "../../api/client";
import { useNavigate } from "react-router-dom";

function pluckArray(payload) {
  if (!payload) return [];
  if (Array.isArray(payload.results)) return payload.results;
  if (Array.isArray(payload.data)) return payload.data;
  if (Array.isArray(payload)) return payload;
  return [];
}

function useDebounced(value, delay = 300) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setV(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return v;
}

// Tiny mobile breakpoint hook
function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < breakpoint : false
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);
  return isMobile;
}

// Seed the grade dropdown with 1–12 so Grade 1 is always available
const GRADE_OPTIONS = Array.from({ length: 12 }, (_, i) => String(i + 1));

// detect whether a value is a numeric id
const isNumericId = (v) => /^\d+$/.test(String(v || ""));

// --- Grade normalizer: "1", "01", "1st", "Grade 1" -> 1; null if invalid/out of range
function normalizeGrade(input) {
  if (input === null || input === undefined) return null;
  const raw = String(input).trim();
  if (/^\d+$/.test(raw)) {
    const n = Number(raw);
    if (Number.isNaN(n)) return null;
    if (n < 0 || n > 12) return null;
    return n;
  }
  const digits = raw.replace(/[^0-9]/g, "");
  if (!digits) return null;
  const n = Number(digits);
  if (Number.isNaN(n)) return null;
  if (n < 0 || n > 12) return null;
  return n;
}

export default function RosterCreatePage() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  // Roster basics
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState("");

  // Student table data
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // Filters + paging
  const [search, setSearch] = useState("");
  const [houseId, setHouseId] = useState("");
  const [grade, setGrade] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  // Dropdown data (derived from current list)
  const [houses, setHouses] = useState([]);
  const [allGrades, setAllGrades] = useState(GRADE_OPTIONS); // ✅ seed with 1–12

  const debouncedSearch = useDebounced(search, 300);

  const query = useMemo(() => {
    const p = { page, page_size: pageSize };
    if (debouncedSearch) p.q = debouncedSearch;
    // IMPORTANT: if the house "id" is actually a name, send 'house' instead of 'house_id'
    if (houseId) {
      if (isNumericId(houseId)) p.house_id = houseId;
      else p.house = houseId;
    }
    if (grade) {
      const ng = normalizeGrade(grade);
      if (ng !== null) p.grade = ng; // SEND NUMERIC
      else p.grade = grade; // fallback
    }
    return p;
  }, [debouncedSearch, houseId, grade, page, pageSize]);

  // Selection
  const [selected, setSelected] = useState(new Set());
  const isSelected = (id) => selected.has(String(id));
  const toggleOne = (id) => {
    const key = String(id);
    const next = new Set(selected);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setSelected(next);
  };
  const togglePage = (ids, allChecked) => {
    const next = new Set(selected);
    const cleanIds = ids.map(String);
    if (allChecked) cleanIds.forEach((id) => next.delete(id));
    else cleanIds.forEach((id) => next.add(id));
    setSelected(next);
  };

  // Load students (robust endpoints)
  const fetchStudents = useCallback(async (params) => {
    const candidates = [
      "/api/students/",
      "/api/students",
      "/api/learners",
      "/api/pupils",
      "/api/users/students",
    ];
    for (const url of candidates) {
      try {
        const res = await api.get(url, { query: params });
        if (res?.ok) {
          const list = pluckArray(res.data);
          const total =
            typeof res.data?.count === "number"
              ? res.data.count
              : typeof res.data?.total === "number"
              ? res.data.total
              : Array.isArray(list)
              ? list.length
              : 0;
          return { ok: true, list, total };
        }
      } catch (_) {
        // keep trying
      }
    }
    return { ok: false, list: [], total: 0 };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const rep = await fetchStudents(query);
      if (!rep.ok) {
        setRows([]);
        setCount(0);
        setErr("Failed to load students.");
        return;
      }
      setRows(rep.list);
      setCount(rep.total);

      // Derive houses + grades from current page
      const hs = new Map();
      const gs = new Set();
      rep.list.forEach((s) => {
        const hid =
          s.house_id ??
          s.houseId ??
          (s.house && typeof s.house !== "string" ? s.house.id : undefined);
        const hname =
          s.house_name ??
          s.houseName ??
          (typeof s.house === "string" ? s.house : s.house?.name);

        if (hid && hname) hs.set(String(hid), String(hname));
        else if (hname && !hid) hs.set(String(hname), String(hname)); // fallback by name

        const gRaw = s.grade ?? s.grade_level ?? s.gradeLevel;
        const ng = normalizeGrade(gRaw);
        if (ng !== null) gs.add(String(ng)); // normalized for dropdown
      });

      setHouses(Array.from(hs, ([id, name]) => ({ id, name })));

      // ✅ Merge discovered grades with seeded 1–12 instead of overwriting
      setAllGrades((prev) => {
        const merged = new Set(prev);
        gs.forEach((g) => merged.add(g));
        return Array.from(merged).sort((a, b) => Number(a) - Number(b));
      });
    } catch (e) {
      setRows([]);
      setCount(0);
      setErr(e?.message || "Unexpected error.");
    } finally {
      setLoading(false);
    }
  }, [fetchStudents, query]);

  // ✅ Now the effect depends on a stable callback
  useEffect(() => {
    load();
  }, [load]);

  // Header checkbox indeterminate
  const idFromRow = (r) => r.id ?? r.student_id ?? r.user_id;
  const visibleIds = rows.map(idFromRow).filter(Boolean);
  const allOnPageChecked =
    visibleIds.length > 0 && visibleIds.every((id) => isSelected(id));
  const someOnPageChecked =
    !allOnPageChecked && visibleIds.some((id) => isSelected(id));
  const headerRef = useRef(null);
  useEffect(() => {
    if (headerRef.current) headerRef.current.indeterminate = someOnPageChecked;
  }, [someOnPageChecked]);

  // Create roster with selected student IDs (robust flow)
  async function createRoster({ name, description, studentIds }) {
    // 1) One-shot create with attached students
    const bodies = [
      { name, description, student_ids: studentIds },
      { name, description, studentIds },
    ];
    for (const body of bodies) {
      try {
        const res = await api.post("/api/rosters", body);
        if (res?.ok) return { ok: true, roster: res.data };
      } catch (_) {}
    }

    // 2) Create then attach (rosters path)
    try {
      const created = await api.post("/api/rosters", { name, description });
      if (created?.ok) {
        const rid = created.data?.id ?? created.data?.roster_id;
        if (rid) {
          const attachCandidates = [
            `/api/rosters/${rid}/students`,
            `/api/rosters/${rid}/members`,
          ];
          for (const url of attachCandidates) {
            try {
              const res = await api.post(url, { student_ids: studentIds });
              if (res?.ok) return { ok: true, roster: created.data };
            } catch (_) {}
          }
          return {
            ok: false,
            roster: created.data,
            error: "Created roster but could not attach students.",
          };
        }
      }
    } catch (_) {}

    // 3) Create then attach (groups path)
    try {
      const created = await api.post("/api/groups", { name, description });
      if (created?.ok) {
        const gid = created.data?.id ?? created.data?.group_id;
        if (gid) {
          const attachCandidates = [
            `/api/groups/${gid}/students`,
            `/api/groups/${gid}/members`,
          ];
          for (const url of attachCandidates) {
            try {
              const res = await api.post(url, { student_ids: studentIds });
              if (res?.ok) return { ok: true, roster: created.data };
            } catch (_) {}
          }
          return {
            ok: false,
            roster: created.data,
            error: "Created group but could not attach students.",
          };
        }
      }
    } catch (_) {}

    return { ok: false, error: "Unable to create roster with provided endpoints." };
  }

  const handleCreate = async () => {
    if (!name.trim()) {
      setSaveErr("Please enter a roster name.");
      return;
    }
    if (selected.size === 0) {
      setSaveErr("Please select at least one student.");
      return;
    }
    setSaveErr("");
    setSaving(true);

    // IMPORTANT: normalize ids to numbers where possible
    const studentIds = Array.from(selected)
      .map((x) => (isNumericId(x) ? Number(x) : x))
      .filter((x) => x !== null && x !== undefined && String(x).trim() !== "");

    const rep = await createRoster({
      name: name.trim(),
      description: description.trim() || undefined,
      studentIds,
    });

    setSaving(false);
    if (!rep.ok) {
      setSaveErr(rep.error || "Failed to create roster.");
      return;
    }
    // success → go back to rosters list or wherever you prefer
    navigate("/rosters", { replace: true });
  };

  const totalPages =
    count && pageSize ? Math.max(1, Math.ceil(count / pageSize)) : null;

  // Compute responsive styles
  const sx = computeStyles(isMobile);

  return (
    <div style={sx.page}>
      <h1 style={sx.h1}>New Roster</h1>

      {/* Roster name + description */}
      <div style={sx.card}>
        <input
          style={sx.input}
          placeholder="Roster Name"
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <textarea
          style={sx.textarea}
          placeholder="Description (optional)"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </div>

      {/* Filters */}
      <div style={sx.filters}>
        <input
          style={sx.input}
          placeholder="Search students by name"
          value={search}
          onChange={(e) => {
            setPage(1);
            setSearch(e.target.value);
          }}
        />
        <select
          style={sx.select}
          value={houseId}
          onChange={(e) => {
            setPage(1);
            setHouseId(e.target.value);
          }}
        >
          <option value="">All Houses</option>
          {houses.map((h) => (
            <option key={h.id} value={h.id}>
              {h.name}
            </option>
          ))}
        </select>
        <select
          style={sx.select}
          value={grade}
          onChange={(e) => {
            setPage(1);
            setGrade(e.target.value);
          }}
        >
          <option value="">All Grades</option>
          {allGrades.map((g) => (
            <option key={g} value={g}>
              {g}
            </option>
          ))}
        </select>
      </div>

      {/* Students table (card with horizontal scroll on mobile) */}
      <div style={sx.tableCard}>
        <div style={sx.tableScroll}>
          <table style={sx.table}>
            <thead style={sx.thead}>
              <tr>
                <th style={sx.thCheckbox}>
                  <input
                    ref={headerRef}
                    aria-label="Select all on page"
                    type="checkbox"
                    checked={allOnPageChecked}
                    onChange={() => togglePage(visibleIds, allOnPageChecked)}
                  />
                </th>
                <th style={sx.th}>Name</th>
                <th style={sx.th}>House</th>
                <th style={sx.th}>Grade</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td style={sx.td} colSpan={4}>
                    Loading…
                  </td>
                </tr>
              )}
              {err && !loading && (
                <tr>
                  <td style={sx.td} colSpan={4}>
                    <span style={sx.error}>{err}</span>
                  </td>
                </tr>
              )}
              {!loading && !err && rows.length === 0 && (
                <tr>
                  <td style={sx.td} colSpan={4}>
                    No students found.
                  </td>
                </tr>
              )}
              {rows.map((r) => {
                const sid = r.id ?? r.student_id ?? r.user_id;
                const displayName =
                  r.name ?? [r.first_name, r.last_name].filter(Boolean).join(" ");
                const house =
                  r.house_name ??
                  r.houseName ??
                  (typeof r.house === "string" ? r.house : r.house?.name) ??
                  "—";
                const gradeVal = r.grade ?? r.grade_level ?? r.gradeLevel ?? "—";
                return (
                  <tr key={String(sid)} style={sx.tr}>
                    <td style={sx.tdCheckbox}>
                      <input
                        type="checkbox"
                        aria-label={`Select ${displayName || "student"}`}
                        checked={isSelected(sid)}
                        onChange={() => toggleOne(sid)}
                      />
                    </td>
                    <td style={sx.td}>{displayName || "—"}</td>
                    <td style={sx.td}>{house}</td>
                    <td style={sx.td}>{gradeVal}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Footer: paging + counts */}
      <div style={sx.footerRow}>
        <div style={sx.footerLeft}>
          {loading
            ? "Loading…"
            : `Showing ${rows.length}${
                typeof count === "number" ? ` of ${count}` : ""
              } students`}
          {selected.size > 0 && (
            <span style={{ marginLeft: 8, fontWeight: 700 }}>
              • Selected: {selected.size}
            </span>
          )}
        </div>
        <div style={sx.footerRight}>
          <label style={sx.footerLabel}>Page Size</label>
          <select
            style={sx.select}
            value={pageSize}
            onChange={(e) => {
              setPage(1);
              setPageSize(Number(e.target.value));
            }}
          >
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
          <button
            style={sx.btnGhost}
            disabled={page <= 1 || loading}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Prev
          </button>
          <button
            style={sx.btnGhost}
            disabled={loading || (totalPages ? page >= totalPages : rows.length < pageSize)}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </button>
        </div>
      </div>

      {/* Save */}
      {saveErr && <div style={sx.error}>{saveErr}</div>}
      <div style={sx.row}>
        <button
          style={sx.btnPrimary}
          onClick={handleCreate}
          disabled={saving || selected.size === 0 || !name.trim()}
        >
          {saving ? "Creating…" : `Create Roster (${selected.size})`}
        </button>
      </div>
    </div>
  );
}

function computeStyles(isMobile) {
  return {
    page: { display: "grid", gap: 16 },
    h1: { margin: 0, fontSize: 20, color: "#003e73", fontWeight: 800 },

    card: {
      background: "#fff",
      border: "1px solid rgba(0,0,0,0.08)",
      borderRadius: 12,
      padding: 12,
      display: "grid",
      gap: 8,
    },
    input: {
      height: 36,
      borderRadius: 8,
      border: "1px solid rgba(0,0,0,0.12)",
      padding: "0 10px",
      width: "100%",
      boxSizing: "border-box",
      fontSize: isMobile ? 16 : 14, // avoid iOS zoom
    },
    textarea: {
      minHeight: 80,
      borderRadius: 8,
      border: "1px solid rgba(0,0,0,0.12)",
      padding: 10,
      width: "100%",
      boxSizing: "border-box",
      fontSize: isMobile ? 16 : 14,
    },

    // Filters collapse to 1 column on mobile
    filters: {
      display: "grid",
      gap: 8,
      gridTemplateColumns: isMobile ? "1fr" : "1fr 200px 160px",
    },
    select: {
      height: 36,
      borderRadius: 8,
      border: "1px solid rgba(0,0,0,0.12)",
      padding: "0 10px",
      background: "#fff",
      width: isMobile ? "100%" : undefined,
      fontSize: isMobile ? 16 : 14,
    },

    // Card container for table (keeps border + rounded corners)
    tableCard: {
      background: "#fff",
      border: "1px solid rgba(0,0,0,0.08)",
      borderRadius: 12,
      overflow: "hidden",
    },

    // Horizontal scroll on narrow screens
    tableScroll: {
      width: "100%",
      overflowX: "auto",
      WebkitOverflowScrolling: "touch",
    },

    table: {
      width: "100%",
      borderCollapse: "collapse",
      minWidth: isMobile ? 520 : "auto", // prevent columns from getting unreadably narrow
    },
    thead: { background: "#F7F9FC" },
    th: {
      textAlign: "left",
      padding: "10px 12px",
      borderBottom: "1px solid rgba(0,0,0,0.08)",
      whiteSpace: "nowrap",
    },
    thCheckbox: {
      width: 40,
      padding: "10px 12px",
      borderBottom: "1px solid rgba(0,0,0,0.08)",
    },
    tr: {},
    td: {
      padding: "10px 12px",
      borderBottom: "1px solid rgba(0,0,0,0.06)",
      whiteSpace: "nowrap",
    },
    tdCheckbox: {
      width: 40,
      padding: "10px 12px",
      borderBottom: "1px solid rgba(0,0,0,0.06)",
    },

    // Footer wraps neatly on phones
    footerRow: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 8,
      flexWrap: isMobile ? "wrap" : "nowrap",
    },
    footerLeft: { color: "#444", flex: isMobile ? "1 1 100%" : "0 0 auto" },
    footerRight: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      flexWrap: isMobile ? "wrap" : "nowrap",
      justifyContent: isMobile ? "flex-start" : "flex-end",
    },
    footerLabel: { fontSize: 13, color: "#555", marginRight: 4 },

    row: {
      display: "flex",
      gap: 8,
      justifyContent: isMobile ? "stretch" : "flex-end",
    },

    btnGhost: {
      height: 32,
      border: "1px solid rgba(0,0,0,0.15)",
      borderRadius: 8,
      padding: "0 10px",
      background: "#fff",
      cursor: "pointer",
      flex: isMobile ? "1 1 auto" : "0 0 auto",
    },
    btnPrimary: {
      height: 40,
      border: "none",
      borderRadius: 8,
      padding: "0 14px",
      background: "#f9a21a",
      color: "#fff",
      fontWeight: 800,
      cursor: "pointer",
      width: isMobile ? "100%" : "auto",
      fontSize: isMobile ? 16 : 14,
    },

    error: {
      color: "#b00020",
      background: "#FFEAEA",
      border: "1px solid #F5B5B5",
      borderRadius: 8,
      padding: "8px 10px",
    },
  };
}
